export interface AdminConfig {
  apiUrl: string;
  /** Lambda Function URL for SSE run streaming (RESPONSE_STREAM). */
  streamUrl: string;
  userPoolId: string;
  clientId: string;
  cognitoDomain: string;
  redirectUri: string;
  siteOrigin: string;
}

interface TokenBundle {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresAt: number;
}

const TOKEN_KEY = 'comita.website.editor.tokens';
const PKCE_VERIFIER_KEY = 'comita.website.editor.pkce';
const PKCE_STATE_KEY = 'comita.website.editor.state';

function cognitoBaseUrl(cognitoDomain: string): string {
  return cognitoDomain.startsWith('http')
    ? cognitoDomain.replace(/\/$/, '')
    : `https://${cognitoDomain}`;
}

function randomString(length: number): string {
  const bytes = crypto.getRandomValues(new Uint8Array(length));
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
}

function sha256Base64Url(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  return crypto.subtle.digest('SHA-256', data).then((hash) => {
    const bytes = new Uint8Array(hash);
    let binary = '';
    bytes.forEach((byte) => {
      binary += String.fromCharCode(byte);
    });
    return btoa(binary)
      .replace(/\+/g, '-')
      .replace(/\//g, '_')
      .replace(/=+$/, '');
  });
}

export function readTokens(): TokenBundle | null {
  const raw = sessionStorage.getItem(TOKEN_KEY);
  if (raw === null) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw) as TokenBundle;
    if (
      typeof parsed.idToken !== 'string' ||
      typeof parsed.expiresAt !== 'number'
    ) {
      return null;
    }
    if (Date.now() >= parsed.expiresAt - 30_000) {
      clearTokens();
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function clearTokens(): void {
  sessionStorage.removeItem(TOKEN_KEY);
}

function writeTokens(tokens: TokenBundle): void {
  sessionStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

export async function beginSignIn(config: AdminConfig): Promise<void> {
  const verifier = randomString(32);
  const state = randomString(16);
  sessionStorage.setItem(PKCE_VERIFIER_KEY, verifier);
  sessionStorage.setItem(PKCE_STATE_KEY, state);
  const challenge = await sha256Base64Url(verifier);
  const params = new URLSearchParams({
    client_id: config.clientId,
    response_type: 'code',
    scope: 'openid email',
    redirect_uri: config.redirectUri,
    code_challenge_method: 'S256',
    code_challenge: challenge,
    state,
  });
  window.location.assign(
    `${cognitoBaseUrl(config.cognitoDomain)}/oauth2/authorize?${params.toString()}`,
  );
}

export async function completeSignInIfNeeded(
  config: AdminConfig,
): Promise<TokenBundle | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  if (code === null) {
    return readTokens();
  }
  const expected = sessionStorage.getItem(PKCE_STATE_KEY);
  if (expected === null || state !== expected) {
    throw new Error('OAuth state mismatch.');
  }
  const verifier = sessionStorage.getItem(PKCE_VERIFIER_KEY);
  if (verifier === null) {
    throw new Error('Missing PKCE verifier.');
  }
  const body = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: config.clientId,
    code,
    redirect_uri: config.redirectUri,
    code_verifier: verifier,
  });
  const response = await fetch(
    `${cognitoBaseUrl(config.cognitoDomain)}/oauth2/token`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: body.toString(),
    },
  );
  if (!response.ok) {
    throw new Error(`Cognito token exchange failed (${response.status}).`);
  }
  const payload = (await response.json()) as {
    access_token: string;
    id_token: string;
    refresh_token?: string;
    expires_in: number;
  };
  const tokens: TokenBundle = {
    accessToken: payload.access_token,
    idToken: payload.id_token,
    refreshToken: payload.refresh_token ?? '',
    expiresAt: Date.now() + payload.expires_in * 1000,
  };
  writeTokens(tokens);
  sessionStorage.removeItem(PKCE_VERIFIER_KEY);
  sessionStorage.removeItem(PKCE_STATE_KEY);
  window.history.replaceState({}, document.title, window.location.pathname);
  return tokens;
}

export function signOut(config: AdminConfig): void {
  clearTokens();
  const params = new URLSearchParams({
    client_id: config.clientId,
    logout_uri: config.redirectUri,
  });
  window.location.assign(
    `${cognitoBaseUrl(config.cognitoDomain)}/logout?${params.toString()}`,
  );
}
